import React, {useCallback, useEffect, useState} from "react";
import {RadioGroup, ReversedRadioButton} from "react-radio-buttons";
import {useForm} from "react-hook-form";
import StepHeader from "./stepParts/StepHeader";
import AgeThresholdQuestions from "./stepParts/AgeThresholdQuestions";
import {
  currencyToNumber,
  getCalculatorType,
  numberToCurrency,
  rewriteRadioOptionsClasses,
  stepStates
} from "../../services/helpers";
import Input from "../core/Input";
import {parseSearchQuery, setField} from "../../store/actions/calculatorActions";
import {assetsBaseUrl} from "../../services/calculationsApi";
import ReactDOM from "react-dom";
import IncomeCalculatorModal from "../modals/IncomeCalculatorModal";
import {useSelector} from "react-redux";
import {gtm} from "../../services/gtm";
import ReactTooltip from "react-tooltip";
import {getStep1HeaderSummary, getStepParams} from "../../services/stepHelpers";
import NextButton from "./stepParts/NextButton";
import {useIntl, FormattedMessage} from 'react-intl';

function Step1({
                 stepNumber,
                 state,
                 loading,
                 openStep,
                 onStepSubmit,
                 dispatch,
                 shouldReset,
                 setResetForms,
                 setShowResetBtn,
               }) {
  const {ApplicantYearlyIncome, PartnerYearlyIncome} =
  useSelector((state) => state.calculator);

  const intl = useIntl();
  const [HasPartner, setHasPartner] = useState();
  const [stepHeaderSummary, setStepHeaderSummary] = useState();
  const [incomeCalculatorVisibilityViaParam, setIncomeCalculatorVisibilityViaParam] = useState(false);
  const [incomeCalculatorVisibility, setIncomeCalculatorVisibility] =
  useState(false);
  const [incomeCalculatorField, setIncomeCalculatorField] = useState(
  "ApplicantYearlyIncome"
  );
  const [params] = useState(() => getStepParams());

  const getDefaultValues = (params) => {
    return {
      ApplicantYearlyIncome: numberToCurrency(params?.ApplicantYearlyIncome, true),
      ApplicantAge: (params?.ApplicantAge === 0) ? '' : params?.ApplicantAge,
      ApplicantAnnualPensionIncome: numberToCurrency(
      params?.ApplicantAnnualPensionIncome, true
      ),
      ApplicantAOW: numberToCurrency(params?.ApplicantAOW, true),
      PartnerYearlyIncome: numberToCurrency(params?.PartnerYearlyIncome, true),
      PartnerAge: (params?.PartnerAge === 0) ? '' : params?.PartnerAge,
      PartnerAnnualPensionIncome: numberToCurrency(
      params?.PartnerAnnualPensionIncome, true
      ),
      PartnerAOW: numberToCurrency(params?.PartnerAOW, true),
    };
  }
  const {
    register,
    handleSubmit,
    formState,
    errors,
    watch,
    triggerValidation,
    setValue,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: getDefaultValues(params),
  });
  const queryParams = parseSearchQuery(window.location.search)
  const isFormValid = formState.isValid && HasPartner;
  const watchAge = watch(["ApplicantAge", "PartnerAge"]); // target specific fields by their names
  rewriteRadioOptionsClasses("buy-house-partner", ["yes", "no"]);

  const toggleIncomeCalculator = useCallback((field) => {
    setIncomeCalculatorField(field);
    setIncomeCalculatorVisibility(!incomeCalculatorVisibility);

    const tagManagerArgs = {
      gtmId: "GTM-T78CX5",
      dataLayer: {
        event: "gtm.virtualPageview",
        virtualURL: `zelf-berekenen/${getCalculatorType()}/jaarinkomen-berekenen`,
        virtualTitle: "Jaarinkomen berekenen",
      },
    };

    gtm(tagManagerArgs);
    //TagManager.initialize(tagManagerArgs);
  }, [incomeCalculatorVisibility]);


  useEffect(() => {
    if (!HasPartner) {
      params?.HasPartner
      ? setHasPartner(params.HasPartner.toString() === "true" ? "1" : "0")
      : setHasPartner("0");
    }
  }, [params, setHasPartner, HasPartner]);

  useEffect(() => {
    if (queryParams?.calculate && !incomeCalculatorVisibilityViaParam) {
      toggleIncomeCalculator('ApplicantYearlyIncome');
      setIncomeCalculatorVisibilityViaParam(true); // we have processed the param
    }

  }, [queryParams, toggleIncomeCalculator, incomeCalculatorVisibilityViaParam, setIncomeCalculatorVisibilityViaParam]);

  useEffect(() => {
    if (shouldReset) {
      reset(getDefaultValues({}));
      setStepHeaderSummary("");
      setResetForms(false);
    }
  }, [shouldReset, reset, setResetForms]);

  useEffect(() => {
    if (ApplicantYearlyIncome) {
      setValue(
      "ApplicantYearlyIncome",
      numberToCurrency(ApplicantYearlyIncome, true),
      {
        shouldDirty: true,
      }
      );
      setShowResetBtn(true);
    } else {
      setShowResetBtn(false);
    }

    PartnerYearlyIncome &&
    setValue(
    "PartnerYearlyIncome",
    numberToCurrency(PartnerYearlyIncome, true),
    {shouldDirty: true}
    );
  }, [ApplicantYearlyIncome, PartnerYearlyIncome, setValue, setShowResetBtn]);

  useEffect(() => {
    params &&
    setStepHeaderSummary(
    getStep1HeaderSummary(
    params?.ApplicantYearlyIncome,
    params?.HasPartner?.toString() === "true",
    params?.PartnerYearlyIncome
    )
    );

  }, [setStepHeaderSummary, params]);

  const onSubmit = (data) => {
    let tagManagerArgs;
    // true or false
    const hasAPartner = !!parseInt(HasPartner);
    tagManagerArgs = {
      gtmId: "GTM-T78CX5",
      dataLayer: {
        event: "gtm.calculators",
        eventCategory: "Calculator",
        eventAction: "Bruto Jaarinkomen",
        eventLabel: hasAPartner ? "met partner" : "zonder partner",
      },
    };

    gtm(tagManagerArgs);
    //console.log(data);
    data.ApplicantAge = Math.floor(data.ApplicantAge);
    data.PartnerAge = Math.floor(data.PartnerAge);
    dispatch(setField("HasPartner", hasAPartner));
    data.ApplicantYearlyIncome = currencyToNumber(
    data.ApplicantYearlyIncome, true
    );
    data.ApplicantAnnualPensionIncome = currencyToNumber(
    data.ApplicantAnnualPensionIncome
    );
    data.ApplicantAOW = currencyToNumber(data.ApplicantAOW);
    data.PartnerYearlyIncome = currencyToNumber(data.PartnerYearlyIncome, true);
    data.PartnerAnnualPensionIncome = currencyToNumber(
    data.PartnerAnnualPensionIncome
    );
    data.PartnerAOW = currencyToNumber(data.PartnerAOW);

    //console.log(data);
    setStepHeaderSummary(
    getStep1HeaderSummary(
    data.ApplicantYearlyIncome,
    hasAPartner,
    data.PartnerYearlyIncome
    )
    );
    onStepSubmit(data, stepNumber);
  };

  const onRadioChange = (value) => {
    setHasPartner(value);
  };

  return (
  <div
  className={`ct-step ${
  state === stepStates.inactive && "ct-step__inactive"
  }
                                 ${
  state === stepStates.filled &&
  "ct-step__filled"
  }
                                 ${
  state === stepStates.closed &&
  "ct-step__closed"
  }`}
  >
    <StepHeader
    title={intl.formatMessage({id: "step1.headerTitle", defaultMessage: "Bruto jaarinkomen"})}
    stepNumber={stepNumber}
    stepSummary={stepHeaderSummary}
    stepState={state}
    openStep={openStep}
    onClose={handleSubmit(onSubmit)}
    />
    <div
    className={`ct-step__body ${
    state === stepStates.active ? "ct-step__body-opened" : null
    }`}
    >
      <form
      name={"step1"}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      >
        <div>
          <div className="ct-step__body-primary-question">
            <FormattedMessage id="step1.yearlyIncomeApplicant" defaultMessage="Wat is je bruto jaarinkomen?"/>
          </div>

          <Input
            placeholder="€"
            type="currency"
            register={register}
            validationRules={{required: true, min: 1.0}}
            name="ApplicantYearlyIncome"
            error={errors.ApplicantYearlyIncome}
            errorTitle={intl.formatMessage({
              id: "step1.yearlyIncome.errorTitle",
              defaultMessage: "bruto jaarinkomen"
            })}
            icon="/assets/svg/calc.svg"
            iconText={intl.formatMessage({
              id: "step1.yearlyIncome.iconText",
              defaultMessage: "Jaarinkomen <br/> berekenen"
            })}
            onIconClick={() =>
            toggleIncomeCalculator("ApplicantYearlyIncome")
            }
          />
        </div>

        <div>
          <div className="ct-step__body-secondary-question">
            <FormattedMessage
            id="step1.ageApplicant"
            defaultMessage="Wat is je leeftijd?"
            />
            <img
            className="ct-step__tool-tip"
            data-tip={intl.formatMessage({
              id: "step1.ageApplicant.ageTooltip",
              defaultMessage: "We vragen je leeftijd omdat we willen weten of je binnen nu en 30 jaar pensioen/AOW zal ontvangen"
            })}
            src={`${assetsBaseUrl}/assets/svg/info.svg`}
            alt=""
            />
            <ReactTooltip place={"right"} effect={"solid"}/>
          </div>

          <Input
          placeholder={intl.formatMessage({
            id: "step1.ageApplicant.placeholder",
            defaultMessage: "30 jaar"
          })}
          type="number"
          className="ct-input__small"
          name="ApplicantAge"
          register={register}
          error={errors.ApplicantAge}
          value={watchAge.ApplicantAge}
          validationRules={{required: true, min: 18}}
          errorTitle="leeftijd"
          valueType="age"
          />
        </div>

        {watchAge.ApplicantAge > 56 &&
        watchAge.ApplicantAge < 67 && (
        <AgeThresholdQuestions
        register={register}
        errors={errors}
        triggerValidation={triggerValidation}
        type={"applicant"}
        />
        )}

        <div className="ct-step__body-secondary-questions-block">
          <div className="ct-step__body-secondary-question ct-step__body-secondary-question--no-tip">

            <FormattedMessage id="step1.withPartner" defaultMessage="Wil je alleen of samen kopen?*"/>
          </div>

          <RadioGroup
          value={HasPartner}
          onChange={onRadioChange}
          horizontal
          className="ct-step__radio-group buy-house-partner"
          >
            <ReversedRadioButton
            value="1"
            rootColor="#000000"
            pointColor="#000000"
            iconInnerSize={18}
            iconSize={18}
            >
              <FormattedMessage id="step1.withPartner.yes" defaultMessage="Samen"/>
            </ReversedRadioButton>

            <ReversedRadioButton
            value="0"
            rootColor="#000000"
            pointColor="#000000"
            iconInnerSize={18}
            iconSize={18}
            >
              <FormattedMessage id="step1.withPartner.no" defaultMessage="Alleen"/>
            </ReversedRadioButton>
          </RadioGroup>
        </div>

        {HasPartner === "1" ? (
        <div className="ct-step__body-secondary-subquestions">
          <div>
            <div className="ct-step__body-subquestion">
              <FormattedMessage id="step1.yearlyIncomePartner" defaultMessage="Wat is het bruto jaarinkomen van je partner?*"/>

            </div>

            <Input
            placeholder="€"
            type="currency"
            register={register}
            validationRules={{required: true, min: 0}}
            name="PartnerYearlyIncome"
            error={errors.PartnerYearlyIncome}
            errorTitle={intl.formatMessage({
              id: "step1.yearlyIncome.errorTitle",
              defaultMessage: "bruto jaarinkomen"
            })}
            icon="/assets/svg/calc.svg"
            iconText={intl.formatMessage({
              id: "step1.yearlyIncome.iconText",
              defaultMessage: "Jaarinkomen <br/> berekenen"
            })}
            onIconClick={() =>
            toggleIncomeCalculator("PartnerYearlyIncome")
            }
            />
          </div>

          <div>

            <div className="ct-step__body-subquestion">
              <FormattedMessage
              id="step1.agePartner"
              defaultMessage="Wat is de leeftijd van je partner?*"
              />
              <img
              className="ct-step__tool-tip"
              data-tip={intl.formatMessage({
                id: "step1.agePartner.ageTooltip",
                defaultMessage: "We vragen de leeftijd omdat we willen weten of je partner binnen nu en 30 jaar pensioen/AOW zal ontvangen"
              })}
              src={`${assetsBaseUrl}/assets/svg/info.svg`}
              alt=""
              />
              <ReactTooltip
              place={"right"}
              effect={"solid"}
              />
            </div>

            <Input
            className="ct-input__small"
            placeholder={intl.formatMessage({
              id: "step1.ageApplicant.placeholder",
              defaultMessage: "30 jaar"
            })}
            type="number"
            name="PartnerAge"
            register={register}
            error={errors.PartnerAge}
            value={watchAge.PartnerAge}
            validationRules={{
              required: true,
              min: 18,
            }}
            errorTitle="leeftijd"
            valueType="age"
            />
          </div>

          {watchAge.PartnerAge > 56 &&
          watchAge.PartnerAge < 67 && (
          <AgeThresholdQuestions
          register={register}
          errors={errors}
          type={"partner"}
          />
          )}
        </div>
        ) : null}

        <NextButton
        loading={loading}
        isFormValid={isFormValid}
        formState={formState}
        ></NextButton>
      </form>
    </div>
    {incomeCalculatorVisibility
    ? ReactDOM.createPortal(
    <IncomeCalculatorModal
    toggleIncomeCalculator={toggleIncomeCalculator}
    field={incomeCalculatorField}
    />,
    document.getElementById("income-calculator-modal-portal")
    )
    : null}
  </div>
  );
}

export default Step1;
